import {  Component,  ElementRef,  EventEmitter,  Input,  OnChanges,  OnInit,  Output,  ViewChild } from '@angular/core';
import {  AbstractControl,  FormBuilder,  FormControl,  FormGroup,  Validators,  ValidatorFn } from '@angular/forms';
import * as _ from 'lodash';

import { Button } from 'primeng/primeng';
import { Claim } from '../../../models/claim.model';
import { GLAdjustmentStage } from '../../../models/glAdjustmentStage.model';
import { GLPolicy } from '../../../models/glPolicy.model';
import { GLPolicyService } from '../../../services/gl-policy.service';
import { GlobalFilterService } from '../../../services/global-filter.service';
import { AdjustmentType, EventSearchGLPolicySelectionComponent } from '../eventsearch-gl-policy-selection/eventsearch-gl-policy-selection.component';
import { EventSearchGLPolicyHelper } from '../helpers/event-search.glPolicyHelper';

@Component({
  selector: 'app-eventsearch-postedclaim-gladjustment',
  templateUrl: './eventsearch-postedclaim-gladjustment.component.html',
  styleUrls: ['./eventsearch-postedclaim-gladjustment.component.css'],
})
export class EventSearchPostedClaimGLAdjustmentComponent
  implements OnInit, OnChanges {
  @Input() selectedClaim: Claim;
  @Input() postedClaimGLAdjustmentModal: boolean;

  @Output() postedClaimGLAdjustmentModalChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onModalAction: EventEmitter<any> = new EventEmitter<any>();

  @ViewChild('glPolicySelection') glPolicySelection: EventSearchGLPolicySelectionComponent;
  @ViewChild('glAdjustmentAmount') glAdjustmentAmount: ElementRef;
  @ViewChild('addAdjustment') addAdjustment: Button

  public adjustmentType: AdjustmentType = AdjustmentType.Claim
  public glPolicyHelper: EventSearchGLPolicyHelper
  public eventLoaded = false;
  public glPolicies: GLPolicy[];

  public selectedPolicy: GLPolicy;
  public remainingBalance = 0.00
  public claimGLAdjustmentForm: FormGroup;
  public glAdjustmentsStage: GLAdjustmentStage[] = [];
  public currentAmount = 0.00

  public hasSelectedPolicy: boolean

    constructor(
    private globalFilterService: GlobalFilterService,
    private glPolicyService: GLPolicyService,
    private fb: FormBuilder,
  ) {}

ngOnInit() {
    this.initForm();
  }

ngOnChanges() {
    if (this.selectedClaim) {
      this.remainingBalance = this.selectedClaim.Money_OpenAmountClaim
      this.currentAmount = this.selectedClaim.Money_OpenAmountClaim
    }
  }

initForm(): void {
    this.claimGLAdjustmentForm = this.fb.group({
      glPolicySelection: new FormControl(null),
      glAdjustmentAmount: new FormControl(null, [
        Validators.required,
        (control: AbstractControl) =>
          this.isValidEntry(this.remainingBalance)(control),
      ]),
    })
    this.glPolicyHelper = new EventSearchGLPolicyHelper()
  }

valueInRange(value: number): boolean {
    let val =
      Math.abs(value) > Math.abs(_.round(this.remainingBalance, 2))
        ? false
        : true;
    return val;
  }

isValidEntry(currentMax: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: boolean } | null => {
      if (!control.value) {
        return null;
      }

      let roundedVal = _.round(control.value, 2);
      // 0 and in range.
      if (roundedVal === 0 || !this.valueInRange(roundedVal)) {
        return { greaterThanRemainingBalance: true };
      }
      if (this.remainingBalance < 0 && control.value > 0) {
        return { negativeValueOnly: true };
      }
      if (this.remainingBalance > 0 && control.value < 0) {
        return { positiveValueOnly: true };
      }
      return null;
    };
  }

addGLAdjustment(): void {
    if (this.hasSelectedPolicy && this.isValidAdjustmentAmount(this.currentAmount)) {
      let newGLAdjustment = new GLAdjustmentStage(this.selectedPolicy.GLCode, this.selectedPolicy.GLPolicyCode, this.selectedPolicy.GLPolicyDescription, this.currentAmount);
      this.glAdjustmentsStage.unshift(newGLAdjustment);
      this.updateRemainingBalance();
      this.resetEntry()
    }
  }

deleteGLAdjustment(index: number): void {
    this.glAdjustmentsStage.splice(index, 1)
    this.updateRemainingBalance()
    this.currentAmount = this.remainingBalance
  }

updateRemainingBalance(): void {
    let summedAmounts = _.round(
      _.sumBy(this.glAdjustmentsStage, (a) => a.amount),
      2,
    );
    this.remainingBalance = _.round(
      this.selectedClaim.Money_OpenAmountClaim - summedAmounts,
      2,
    );
  }

isValidAdjustmentAmount(value: number): boolean {
    if (value === 0) {
      return false;
    }

    let sign = value < 0 ? true : false;

    let glAdjustmentsSum =
      _.sumBy(this.glAdjustmentsStage, (a) => a.amount) + value
    glAdjustmentsSum = _.round(glAdjustmentsSum, 2)
    if (
      glAdjustmentsSum > this.selectedClaim.Money_OpenAmountClaim ||
      value > this.remainingBalance
    ) {
      return sign;
    }
    return true;
  }

onPolicyChanged(policy: GLPolicy): void {
    this.hasSelectedPolicy = true
    this.selectedPolicy = policy
  }

onAmountChanged(value: string): void {
  if (this.claimGLAdjustmentForm.invalid) {
    return
  }
  if (value) {
    let amount = Number.parseFloat(value)

    if (!Number.isNaN(amount) && this.isValidAdjustmentAmount(amount)) {
      this.currentAmount = amount
      this.addGLAdjustment()
    }
  }
}

cancelClick(): void {
    this.resetData();
    this.updateRemainingBalance();
    this.postedClaimGLAdjustmentModal = false;
    this.postedClaimGLAdjustmentModalChange.emit(this.postedClaimGLAdjustmentModal )
    this.onModalAction.emit({ type: 'cancel' });
  }

resetEntry(): void {
  this.hasSelectedPolicy = false
  this.selectedPolicy = null
  this.glPolicySelection.reset()
  this.currentAmount = this.remainingBalance
}

resetData(): void {
  this.resetEntry()
  this.glAdjustmentsStage = [];
  this.currentAmount = this.selectedClaim.Money_OpenAmountClaim
  this.postedClaimGLAdjustmentModal = false;
  this.postedClaimGLAdjustmentModalChange.emit(this.postedClaimGLAdjustmentModal )

}
submitClick(): void {
    //  Convert the set of staged GL Adjustments to the structure sent to the iSeries.
    let glAdjustments = this.glPolicyHelper.toGLAdjustments(this.glAdjustmentsStage)

    this.onModalAction.emit({ type: 'submit', value: glAdjustments });
    this.resetData();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/event-search/eventsearch-postedclaim-gladjustment/eventsearch-postedclaim-gladjustment.component.ts